import React, { useCallback, useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { fetchVideos, addVideo, deleteVideo } from './api';
import Header from './Header';
import FilmStrip from './FilmStrip';

const swalDark = {
  background: '#171310',
  color: '#f0e9df',
  confirmButtonColor: '#e13a2e',
  cancelButtonColor: '#3a2f26',
};

// Přijme buď celou URL (youtube.com/watch?v=ID, youtu.be/ID, shorts/ID) nebo rovnou samotné ID.
function extractYoutubeId(input) {
  const value = (input || '').trim();
  if (!value) return null;

  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtube\.com\/shorts\/|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/,
  ];

  for (const pattern of patterns) {
    const match = value.match(pattern);
    if (match) return match[1];
  }

  // Vypadá to už jako holé ID
  if (/^[\w-]{11}$/.test(value)) return value;

  return null;
}

export default function Videos({ onBack, onLogout }) {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [urlInput, setUrlInput] = useState('');
  const [titleInput, setTitleInput] = useState('');
  const [saving, setSaving] = useState(false);

  const loadVideos = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await fetchVideos();
      if (!data.success) throw new Error(data.message || 'Načtení se nezdařilo.');
      setVideos(data.videos || []);
    } catch (err) {
      setLoadError(err.message || 'Nepodařilo se spojit se serverem.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVideos();
  }, [loadVideos]);

  const handleAdd = async (e) => {
    e.preventDefault();
    const youtubeId = extractYoutubeId(urlInput);

    if (!youtubeId) {
      Swal.fire({
        ...swalDark,
        title: 'Nepodařilo se poznat video',
        text: 'Vlož celý odkaz z YouTube (např. https://www.youtube.com/watch?v=...) nebo jeho ID.',
        icon: 'warning',
        confirmButtonColor: '#d9932f',
      });
      return;
    }

    setSaving(true);
    try {
      const res = await addVideo(youtubeId, titleInput.trim());
      if (!res.success) throw new Error(res.message || 'Přidání se nezdařilo.');
      setUrlInput('');
      setTitleInput('');
      await loadVideos();
      Swal.fire({
        ...swalDark,
        title: 'Video přidáno',
        icon: 'success',
        timer: 1300,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        ...swalDark,
        title: 'Chyba',
        text: err.message || 'Přidání se nezdařilo.',
        icon: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (video) => {
    const result = await Swal.fire({
      ...swalDark,
      title: 'Smazat video?',
      html: `Opravdu chceš odebrat <strong>${video.title || video.youtube_id}</strong>?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Smazat',
      cancelButtonText: 'Zrušit',
      reverseButtons: true,
      focusCancel: true,
    });

    if (!result.isConfirmed) return;

    try {
      const res = await deleteVideo(video.id);
      if (!res.success) throw new Error(res.message || 'Smazání se nezdařilo.');
      setVideos((prev) => prev.filter((v) => v.id !== video.id));
    } catch (err) {
      Swal.fire({
        ...swalDark,
        title: 'Chyba',
        text: err.message || 'Smazání se nezdařilo.',
        icon: 'error',
      });
    }
  };

  return (
    <div className="page">
      <Header eyebrow="Kontaktní arch" title="Videa" onBack={onBack} onLogout={onLogout} />
      <FilmStrip />

      <main className="main">
        <form className="add-form" onSubmit={handleAdd}>
          <p className="add-form__title">Přidat nové video</p>
          <input
            type="text"
            className="add-form__input"
            placeholder="Vlož odkaz z YouTube"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            disabled={saving}
          />
          <input
            type="text"
            className="add-form__input"
            placeholder="Název videa (nepovinné)"
            value={titleInput}
            onChange={(e) => setTitleInput(e.target.value)}
            disabled={saving}
          />
          <button className="btn add-form__submit" type="submit" disabled={saving}>
            {saving ? 'Přidávám…' : 'Přidat video'}
          </button>
        </form>

        {loadError && (
          <div className="state state--error">
            <p>{loadError}</p>
            <button className="btn btn--ghost" onClick={loadVideos}>Zkusit znovu</button>
          </div>
        )}

        {!loadError && loading && (
          <div className="state">
            <p>Načítám videa…</p>
          </div>
        )}

        {!loadError && !loading && videos.length === 0 && (
          <div className="state">
            <p>Zatím tu nejsou žádná videa.</p>
            <p className="state__sub">Vlož odkaz na YouTube video výše.</p>
          </div>
        )}

        {!loadError && !loading && videos.length > 0 && (
          <div className="grid">
            {videos.map((video) => (
              <article className="frame" key={video.id}>
                <div className="frame__photo">
                  <img
                    src={`https://img.youtube.com/vi/${video.youtube_id}/hqdefault.jpg`}
                    alt={video.title || 'Video'}
                    loading="lazy"
                  />
                </div>
                <div className="frame__meta">
                  <p className="frame__name" title={video.title || video.youtube_id}>
                    {video.title || video.youtube_id}
                  </p>
                </div>
                <button className="frame__delete" onClick={() => handleDelete(video)}>
                  Smazat
                </button>
              </article>
            ))}
          </div>
        )}
      </main>

      <footer className="footer">
        <span className="footer__dot" /> Připojeno k backendu
      </footer>
    </div>
  );
}
